import { formatCount, formatDate } from './utils';

const MAX_COURSE_REQUEST_PAGES = 10;

const isTestEmail = (email, domains = []) => {
  const domain = String(email || '').split('@')[1]?.toLowerCase();
  return !!domain && domains.some(item => String(item).toLowerCase() === domain);
};

const overAssignedProjects = async (data, service) => {
  const customers = (data.customers?.top || []).filter(row => row.over_assigned && row.customer_id);
  const details = await Promise.all(customers.map(customer => (
    service.getSuperadminCustomerInsights(customer.customer_id)
      .then(response => ({ customer, projects: response?.data?.projects || [] }))
      .catch(() => ({ customer, projects: [] }))
  )));
  return details.flatMap(({ customer, projects }) => projects
    .filter(project => project.over_assigned)
    .map(project => ({
      id: project.project_id,
      label: project.project,
      sublabel: `${customer.name} · ${formatCount(project.seats_assigned)} assigned to ${formatCount(project.seats_allocated)} ${Number(project.seats_allocated) === 1 ? 'seat' : 'seats'}`,
      customer_id: customer.customer_id,
      project_id: project.project_id,
    })));
};

const pendingCourseRequests = async (data, service) => {
  const testDomains = data.meta?.test_email_domains || [];
  const rows = [];
  for (let page = 1; page <= MAX_COURSE_REQUEST_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await service.getCourseRequestsPage(page);
    rows.push(...(response?.results || []));
    if (!response?.next) break;
  }
  return rows
    .filter(row => row.status === 'pending' && !isTestEmail(row.user?.email, testDomains))
    .map(row => {
      const learner = [row.user?.firstName, row.user?.lastName].filter(Boolean).join(' ') || row.user?.userName || 'Learner';
      return {
        id: row.uuid,
        label: row.course?.name || 'Course request',
        sublabel: `${learner} · ${row.user?.email || 'no email'} · requested ${formatDate(row.created_at)}`,
        target: { page: 'course_requests', search: row.user?.email || row.course?.name },
      };
    });
};

const DERIVED_RESOLVERS = {
  over_assigned_licences: overAssignedProjects,
  pending_course_requests: pendingCourseRequests,
};

/**
 * Returns `{ records, source }` for one attention item.
 * source is 'api' when the API listed the records, 'derived' when they were worked out from
 * other endpoints (counts can differ from the API's headline number), or 'none'.
 */
export const resolveAttentionRecords = async (item, data, service) => {
  if (Array.isArray(item?.records)) return { records: item.records, source: 'api' };
  const resolver = DERIVED_RESOLVERS[item?.key];
  if (!resolver) return { records: [], source: 'none' };
  return { records: await resolver(data || {}, service), source: 'derived' };
};

export const canResolveRecords = item => Array.isArray(item?.records) || !!DERIVED_RESOLVERS[item?.key];
